import { memo, type ReactNode } from 'react'
import styled from 'styled-components'
import { Button } from '@shipt/design-system-buttons'
import { useOnClearAllFilters } from '@/features/shop/components/ProductFilter/interactions/useOnClearAllFilters'
import { Row } from '@shipt/design-system-layouts'
import { useFiltersContext } from '@/features/shop/components/ProductFilter/FiltersContext'
import { ArrowLeftIcon } from '@shipt/design-system-icons'
import { routes } from '@shared/constants/routes'
import { trackElementClicked } from '@/features/shared/analytics/element'
import { TextButtonLink } from '@/features/shared/elements/Link/Buttons'
import { stringifyWithCatIdsArray } from '@/features/shared/utils/queryString'
import { screenSizes } from '@shipt/design-system-themes'
import { useSearchQueryParams } from '@/features/shared/hooks/search'
import { hideScrollBar } from '@/features/shared/styles/effects'

type Props = {
  children: ReactNode
  className?: string
}

export const FilterOptions = memo(({ children, className }: Props) => {
  const { facets = [] } = useFiltersContext()
  const { showClearButton, onClearAllFilters } = useOnClearAllFilters(facets)
  const { referrer = '', category_ids, ...routerQuery } = useSearchQueryParams()
  const isMultiStoreSearch = referrer === routes.GLOBAL_SEARCH.trackingName

  const { category_name } = routerQuery
  const allResultsText = typeof category_name === 'string' ? category_name : 'All Results'
  return (
    <OptionsWrapper className={className}>
      <Wrapper>
        {!!isMultiStoreSearch && (
          <BackToResultsLink
            onClick={() => {
              trackElementClicked({
                link_name: 'all_results',
              })
            }}
            href={`${routes.GLOBAL_SEARCH.url}?${stringifyWithCatIdsArray(
              routerQuery,
              String(category_ids ?? ''),
            )}`}
            aria-label={`back to ${allResultsText}`}
            leadingIcon={ArrowLeftIcon}
          >
            {allResultsText}
          </BackToResultsLink>
        )}
        <Row align="center">{children}</Row>
        {!!showClearButton && (
          <ResetButton
            size="sm"
            onClick={onClearAllFilters}
            aria-label="reset all filters"
            variant="ghost"
          >
            Reset
          </ResetButton>
        )}
      </Wrapper>
    </OptionsWrapper>
  )
})

const OptionsWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px var(--padding-inline, 16px) 8px;
  overflow: auto hidden;
  min-height: 3.75rem;

  ${hideScrollBar}

  @media ${screenSizes.tablet} {
    overflow: auto;
    padding: 8px 0 0;
  }
`
const Wrapper = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row-reverse;

  @media ${screenSizes.tablet} {
    flex-flow: row wrap;
  }
`
const BackToResultsLink = styled(TextButtonLink)`
  margin-right: 0.5rem;
  text-transform: capitalize;

  @media ${screenSizes.max_mobile} {
    display: none;
  }
`
const ResetButton = styled(Button)`
  justify-content: center;
  height: 2.25rem;
  margin-right: 0.25rem;

  @media ${screenSizes.tablet} {
    margin-left: 1rem;
    margin-right: 0;
  }
`
